<script setup>
import { defineProps, ref } from 'vue'
import { provideDocExpansion } from '@/composables/useDocExpansion'
import DocSection from '@/components/Docs/Section.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'
import EndpointBlock from '@/components/Docs/EndpointBlock.vue'
import BasicAttribute from '@/components/Docs/Attributes/BasicAttribute.vue'

defineProps({ anchor: String })

const { triggerExpandAll } = provideDocExpansion()
const allExpanded = ref(false)
const toggleAllExpansion = () => {
  allExpanded.value = !allExpanded.value
  triggerExpandAll(allExpanded.value)
}
</script>

<template>
  <doc-section title="IP schema" :anchor="anchor">
    <template v-slot:left>
      <div class="mb-5">
        <p>
          Returns a machine-readable description of the IP enrichment surface: the
          common fields present on every enriched row plus the catalog of available
          enrichments and the fields each one produces. It takes no parameters and
          is the self-documenting companion to the IP lookup endpoints, useful for
          discovering which enrichment slugs you can pass to
          <a href="#ip-lookup">IP lookup</a>.
        </p>
      </div>

      <div>
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h4 class="mb-0">Response attributes</h4>
          <button @click="toggleAllExpansion" class="btn-expand-collapse">
            <font-awesome-icon :icon="['fas', allExpanded ? 'compress-alt' : 'expand-alt']" />
            {{ allExpanded ? 'Collapse All' : 'Expand All' }}
          </button>
        </div>

        <BasicAttribute name="status" type="string" description="The status of the request's response" />

        <BasicAttribute name="data" type="object" description="The IP schema" :depth="1">
          <BasicAttribute name="entity_type" type="string" description="Always 'ip' for this endpoint" :depth="2" />
          <BasicAttribute name="common_fields" type="array" description="Fields present on every enriched IP row, independent of enrichment" :depth="2" />
          <BasicAttribute name="enrichments" type="array" description="Catalog of available enrichments" :depth="2">
            <BasicAttribute name="slug" type="string" description="The enrichment identifier passed to the 'enrichments' query parameter" />
            <BasicAttribute name="name" type="string" description="Human-readable enrichment name" />
            <BasicAttribute name="description" type="string" description="What the enrichment provides" />
            <BasicAttribute name="response_fields" type="array" description="The fields this enrichment adds to a result" />
          </BasicAttribute>
        </BasicAttribute>
      </div>
    </template>

    <template v-slot:right>
      <endpoint-block
        title="/query/schema/ip"
        method="GET"
        name="IP Schema"
        query="/query/schema/ip"
      />

      <json-block title="Response">
{
  "status": "success",
  "data": {
    "entity_type": "ip",
    "common_fields": [
      { "name": "ip", "type": "string", "description": "The target IP address" }
    ],
    "enrichments": [
      {
        "slug": "geo",
        "name": "Geolocation",
        "description": "Location, network, AS, company and hostname intelligence",
        "response_fields": [
          { "name": "location.country_iso", "type": "string" },
          { "name": "as.as_number", "type": "integer" }
        ]
      },
      {
        "slug": "anonymization",
        "name": "Anonymization",
        "description": "VPN / proxy / Tor and anonymization service attribution",
        "response_fields": [
          { "name": "vpn", "type": "boolean" },
          { "name": "services", "type": "array" }
        ]
      }
    ]
  }
}
      </json-block>
    </template>
  </doc-section>
</template>

<style scoped>
.btn-expand-collapse {
  background-color: transparent;
  border: 1px solid var(--border-subtle);
  color: var(--text-faint);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-md);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-expand-collapse:hover {
  background-color: var(--surface-overlay-2);
  color: var(--text-primary);
}
</style>
